import { useForm } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React from 'react';
import { z } from 'zod';
import TablerIconBook from '~icons/tabler/book';
import TablerIconHome from '~icons/tabler/home';
import TablerIconRocket from '~icons/tabler/rocket';

import { Alert, Button, Divider, Fade, MultiSelect, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { ImgLogo } from '#/components/images';
import { ButtonLink } from '#/components/links';
import { CustomerLogoList, CustomerTestimonialCard } from '#/components/widgets';
import fx from '#/fx';
import { useAnalytics } from '#/lib/analytics';
import { canonicalLinkTag, seo } from '#/lib/head';
import { cn } from '#/utils/classnames';

export const Route = createFileRoute('/_blank/signup')({
  validateSearch: (search: Record<string, unknown>): { plan?: string } => {
    return {
      plan: (search.plan as SignupPlan) || 'starter',
    };
  },
  loaderDeps: ({ search }) => ({
    plan: (search.plan ?? 'starter') as SignupPlan,
  }),
  loader: async (ctx) => {
    return load(ctx.deps.plan);
  },
  head: ({ match, loaderData }) => ({
    meta: seo({
      ...loaderData?.seo,
      title: loaderData?.seo?.title ?? 'Signup',
      url: match.pathname,
    }),
    links: [canonicalLinkTag(match.pathname)],
  }),
  component: ContactPage,
});

async function load(plan: SignupPlan) {
  const page = await fx.getPageContentBySlug({
    data: {
      key: 'signup',
      slug: plan,
    },
  });

  const testimonials = await fx.listCustomerTestimonials({
    data: {
      fields: {
        ids: [{ id: page.sections.sidebar.featuredTestimonialId }],
      },
    },
  });
  const customerLogos = await fx.listCustomerLogos({
    data: {
      fields: {
        ids: page.sections.sidebar.logos.map((id, i) => ({ id, order: i })),
      },
    },
  });
  return {
    ...page,
    sections: {
      ...page.sections,
      sidebar: {
        ...page.sections.sidebar,
        featuredTestimonial: testimonials[0],
        customerLogos: customerLogos.map((logo) => logo),
      },
    },
  };
}

type LoaderData = Awaited<ReturnType<typeof load>>;

function ContactPage() {
  const page = Route.useLoaderData();
  return (
    <div className='w-full h-full min-h-screen bg-background-default'>
      <div className='w-full'>
        <div
          className={cn(
            'w-[600px] min-h-screen items-center justify-center flex-col px-4 bg-background-paper',
            'fixed left-0 top-0 bottom-0',
            'hidden lg:flex'
          )}
        >
          <div
            className='z-0 brightness-40 absolute inset-0 h-full w-full'
            style={{
              backgroundImage: 'url(/images/backgrounds/dots-white.png)',
            }}
          />
          <Fade in>
            {(transitionProps) => (
              <div {...transitionProps}>
                <PageSectionSidebar sections={page.sections} />
              </div>
            )}
          </Fade>
        </div>
        <div
          className={cn(
            'w-full lg:ml-[600px] min-h-screen flex relative items-center justify-center',
            'lg:w-[calc(100%-600px)]'
          )}
        >
          <Fade in>
            {(transitionProps) => (
              <div {...transitionProps}>
                <div className='absolute top-6 left-6 right-6 flex items-center justify-center'>
                  <Stack direction='row' spacing={2}>
                    <ImgLogo logo='contrast' className='max-w-[100px] h-auto mx-auto mb-4' />
                    <div className='grow' />
                    <ButtonLink
                      size='small'
                      variant='text'
                      color='primary'
                      startIcon={<TablerIconBook />}
                      href='https://docs.joggr.io'
                      openInNewTab
                    >
                      Documentation
                    </ButtonLink>
                  </Stack>
                </div>
                <div className='px-4 py-24 lg:py-0 lg:pb-0 lg:px-0'>
                  <PageSectionBooking sections={page.sections} />
                </div>
              </div>
            )}
          </Fade>
        </div>
      </div>
    </div>
  );
}

/*
|------------------
| Internals
|------------------
*/

function PageSectionBooking({ sections }: SignupPageSectionProps) {
  const [showError, setShowError] = React.useState(false);
  const [step, setStep] = React.useState<'email' | 'questions'>('email');
  const analytics = useAnalytics();
  const nav = useNavigate();
  const search = Route.useSearch();
  const { questions } = sections.form;

  const content = React.useMemo(() => {
    return {
      title: sections.form[step].title,
      description: sections.form[step].description,
    };
  }, [step, sections.form]);

  const form = useForm({
    defaultValues: {
      email: '',
      existingDocumentation: [],
      existingDiagrams: [],
      frustrations: [],
    } as {
      email: string;
      existingDocumentation: string[];
      existingDiagrams: string[];
      frustrations: string[];
    },
    onSubmit: async ({ value, formApi }) => {
      setShowError(false);
      try {
        analytics.identify({ email: value.email });

        await fx.sendSignupMessage({
          data: {
            email: value.email,
            existingDocumentation: value.existingDocumentation ?? [],
            existingDiagrams: value.existingDiagrams ?? [],
            frustrations: value.frustrations ?? [],
            plan: search.plan,
            posthogSessionId: analytics.getSessionId(),
          },
        });
        formApi.reset();
        nav({ to: '/signup/confirmation', search: { plan: search.plan } });
      } catch (error) {
        console.error(error);
        setShowError(true);
      }
    },
    validators: {
      onSubmit: signupDataSchema,
    },
  });

  return (
    <Stack
      component='form'
      id='signup-form'
      direction='column'
      spacing={1}
      className='max-w-[480px] w-full mx-auto'
      justifyContent='start'
      alignContent='start'
      alignItems='start'
      onKeyUp={(e) => {
        // We only want to handle Enter key presses
        // as we are stubbing out the submit button
        if (e.key !== 'Enter') {
          return;
        }

        if (
          step === 'email' &&
          !!form.getFieldValue('email') &&
          form.getFieldMeta('email')?.errors.length === 0
        ) {
          e.preventDefault();
          e.stopPropagation();
          setStep('questions');
        }
      }}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        if (
          step === 'email' &&
          !!form.getFieldValue('email') &&
          form.getFieldMeta('email')?.errors.length === 0
        ) {
          setStep('questions');
        }

        if (step === 'questions') {
          form.handleSubmit();
        }
      }}
    >
      <Typography variant='h3' className='text-center mx-auto'>
        {content.title}
      </Typography>
      <Typography variant='body1' className='text-center mx-auto'>
        {content.description}
      </Typography>
      <Divider className='w-full my-4' />
      {showError && (
        <Alert className='w-full' severity='error' onClose={() => setShowError(false)}>
          You were unable to signup, please try again. If this issue persists, please contact us at{' '}
          <a href='mailto:support@joggr.com'>support@joggr.com</a>.
        </Alert>
      )}
      <form.Field
        name='email'
        validators={{
          onChange: z.string().email('Must be valid email'),
        }}
      >
        {(field) => (
          <fieldset
            className={cn('d-fieldset w-full', {
              hidden: step !== 'email',
            })}
          >
            <input
              type='text'
              className='d-input d-input-primary w-full'
              placeholder='Enter your company email'
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          </fieldset>
        )}
      </form.Field>
      {questions.fields.docs.include && (
        <form.Field name='existingDocumentation'>
          {(field) => (
            <span
              className={cn({
                hidden: step !== 'questions',
              })}
            >
              <SignupFormMultiselect
                label={questions.fields.docs.label}
                value={field.state.value}
                options={questions.fields.docs.options}
                other={questions.fields.docs.other}
                onChange={(value) => {
                  field.handleChange(value);
                }}
              />
            </span>
          )}
        </form.Field>
      )}
      {questions.fields.diagrams.include && (
        <form.Field name='existingDiagrams'>
          {(field) => (
            <span
              className={cn({
                hidden: step !== 'questions',
              })}
            >
              <SignupFormMultiselect
                label={questions.fields.diagrams.label}
                options={questions.fields.diagrams.options}
                value={field.state.value}
                other={questions.fields.diagrams.other}
                onChange={(value) => {
                  field.handleChange(value);
                }}
              />
            </span>
          )}
        </form.Field>
      )}
      {questions.fields.frustrations.include && (
        <form.Field name='frustrations'>
          {(field) => (
            <span
              className={cn({
                hidden: step !== 'questions',
              })}
            >
              <SignupFormMultiselect
                label={questions.fields.frustrations.label}
                options={questions.fields.frustrations.options}
                value={field.state.value}
                other={questions.fields.frustrations.other}
                onChange={(value) => {
                  field.handleChange(value);
                }}
              />
            </span>
          )}
        </form.Field>
      )}
      <div className='w-full mt-4'>
        <form.Subscribe
          selector={(state) => [
            state.fieldMeta.email?.errors.length === 0 ? state.values.email : null,
          ]}
        >
          {([email]) => (
            <Button
              type='button'
              variant='contained'
              color='primary'
              className={cn({
                hidden: step !== 'email',
              })}
              disabled={!email}
              startIcon={<TablerIconRocket />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (email) {
                  setStep('questions');
                  analytics.identify({ email });
                }
              }}
              fullWidth
            >
              Continue
            </Button>
          )}
        </form.Subscribe>
        <form.Subscribe
          selector={(state) => [
            [
              !!state.values.email && state.fieldMeta.email?.errors.length === 0,
              state.values.existingDocumentation.length > 0,
              // state.values.existingDiagrams.length > 0,
              state.values.frustrations.length > 0,
            ].every((x) => x === true),
            state.isSubmitting,
          ]}
        >
          {([canSubmit, isSubmitting]) => (
            <Button
              type='submit'
              variant='contained'
              color='primary'
              className={cn({
                hidden: step !== 'questions',
              })}
              startIcon={<TablerIconRocket />}
              disabled={!canSubmit}
              loading={isSubmitting}
              fullWidth
            >
              Signup for Joggr
            </Button>
          )}
        </form.Subscribe>
      </div>
    </Stack>
  );
}

function PageSectionSidebar({ sections }: SignupPageSectionProps) {
  return (
    <Stack className='z-10' alignItems='center' spacing={3}>
      <div className='max-w-[480px] w-full z-20'>
        <GlowCard border='top'>
          <Typography variant='h6' className='text-center mb-8'>
            Trusted by the best software teams
          </Typography>
          <CustomerLogoList
            customerLogos={sections.sidebar.customerLogos}
            opacity={0.8}
            className=' [&>.quark-customer-logo]:w-[45%]'
          />
        </GlowCard>
      </div>
      <div className='max-w-[480px] w-full z-20'>
        <CustomerTestimonialCard testimonial={sections.sidebar.featuredTestimonial} />
      </div>
      <ButtonLink
        startIcon={<TablerIconHome />}
        to='/'
        variant='outlined'
        color='primary'
        className='mt-8 bg-slate-900 hover:bg-slate-800 z-20'
        size='large'
      >
        Go back to the homepage
      </ButtonLink>
    </Stack>
  );
}

type SignupPageSectionProps = Pick<LoaderData, 'sections'>;

type SignupFormMultiselectProps = {
  label: string;
  value: string[];
  options: Array<{ id: string; label: string }>;
  other: boolean;
  onChange: (value: string[]) => void;
};

function SignupFormMultiselect({
  label,
  value,
  options,
  other,
  onChange,
}: SignupFormMultiselectProps) {
  return (
    <Stack direction='column' spacing={1}>
      <Typography variant='body1'>{label}</Typography>
      <MultiSelect
        options={options}
        value={value}
        allowOther={other}
        onChange={(value) => {
          onChange(value);
        }}
      />
    </Stack>
  );
}

const signupDataSchema = z.object({
  email: z.string().email('Must be valid email'),
  existingDocumentation: z.array(z.string()),
  existingDiagrams: z.array(z.string()),
  frustrations: z.array(z.string()),
});

type SignupPlan = 'starter' | 'growth' | 'scale';
